import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import type {
  ChartCardLayout,
  ExportJobView,
  LatestReadingsResponse,
  MetricKey,
  ReadingsRange,
  WellReadingsResponse,
  WellsHierarchy,
} from './types';

// Slow poll for live-ish charts. Window-focus refetch does most of the work;
// this is a backstop for a tab left open. Env-overridable per environment.
const CHART_POLL_MS = Number(import.meta.env.VITE_WELLS_CHART_POLL_MS) || 60_000;

const wellsKeys = {
  all: ['wells'] as const,
  hierarchy: () => [...wellsKeys.all, 'hierarchy'] as const,
  latest: () => [...wellsKeys.all, 'latest'] as const,
  readings: (id: string, range: ReadingsRange, metrics?: MetricKey[]) =>
    [...wellsKeys.all, 'readings', id, range, metrics ?? null] as const,
};

// Exported so the charts section can invalidate all readings on manual refresh.
export const wellsReadingsQueryKey = [...wellsKeys.all, 'readings'] as const;
const chartCardsKey = ['me', 'chart-cards'] as const;

// The customer's sites + wells + latest-reading timestamps, grouped.
export function useWells() {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellsKeys.hierarchy(),
    enabled: Boolean(accessToken),
    queryFn: () => api.request<WellsHierarchy>('/wells'),
  });
}

// Newest reading per reporting well — drives the summary cards.
export function useLatestReadings() {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellsKeys.latest(),
    enabled: Boolean(accessToken),
    queryFn: () => api.request<LatestReadingsResponse>('/wells/latest'),
  });
}

// Range-filtered readings for one well. Built now for Chunk 5's charts to
// consume; this chunk's UI doesn't chart yet. Fires only with a complete range.
export function useWellReadings(
  id: string | undefined,
  range: ReadingsRange | undefined,
  metrics?: MetricKey[],
) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellsKeys.readings(id ?? '', range ?? { from: '', to: '' }, metrics),
    enabled: Boolean(id) && Boolean(range?.from) && Boolean(range?.to) && Boolean(accessToken),
    // Slow polling + refetch-on-focus keeps charts live without hammering the
    // API. React Query dedupes identical keys, so cards on the same well+range
    // share one request.
    refetchInterval: CHART_POLL_MS,
    refetchOnWindowFocus: true,
    queryFn: () =>
      api.request<WellReadingsResponse>(`/wells/${id}/readings`, {
        query: {
          from: range!.from,
          to: range!.to,
          metrics: metrics && metrics.length > 0 ? metrics.join(',') : undefined,
        },
      }),
  });
}

// Per-user chart layout. Never 404s — the backend returns a default for a
// first-time user.
export function useChartCards() {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: chartCardsKey,
    enabled: Boolean(accessToken),
    queryFn: () => api.request<ChartCardLayout>('/me/chart-cards'),
  });
}

export function useSaveChartCards() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layout: ChartCardLayout) =>
      api.request<ChartCardLayout>('/me/chart-cards', { method: 'PUT', body: layout }),
    onSuccess: (saved) => {
      // Keep the cache in step with the persisted layout without a refetch.
      qc.setQueryData(chartCardsKey, saved);
    },
  });
}

// --- CSV export ---

const exportsKeys = {
  all: ['exports'] as const,
  job: (id: string) => [...exportsKeys.all, 'job', id] as const,
  forWell: (wellId: string) => [...exportsKeys.all, 'well', wellId] as const,
};

export function useCreateExport() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { wellId: string; from: string; to: string }) =>
      api.request<{ jobId: string }>(`/wells/${input.wellId}/export`, {
        method: 'POST',
        body: { from: input.from, to: input.to },
      }),
    onSuccess: (_data, vars) =>
      void qc.invalidateQueries({ queryKey: exportsKeys.forWell(vars.wellId) }),
  });
}

// Polls a single job while pending/running, then stops. On complete the response
// carries the freshly-minted downloadUrl.
export function useExportJob(jobId: string | null) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: exportsKeys.job(jobId ?? ''),
    enabled: Boolean(jobId) && Boolean(accessToken),
    queryFn: () => api.request<ExportJobView>(`/exports/${jobId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 1500 : false;
    },
  });
}

export function useWellExports(wellId: string | null) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: exportsKeys.forWell(wellId ?? ''),
    enabled: Boolean(wellId) && Boolean(accessToken),
    queryFn: () => api.request<{ jobs: ExportJobView[] }>(`/wells/${wellId}/exports`),
  });
}

// Fetch a completed job to mint a fresh download URL (server audits the
// download on this GET), then the caller opens it.
export function useExportDownload() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (jobId: string) => api.request<ExportJobView>(`/exports/${jobId}`),
  });
}
